import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";

import * as Sentry from "@sentry/gatsby";

// Utils
import { analytics as analyticsService } from "./src/utils/analytics";
import { currentPageData } from "./src/utils/currentPageData";
import { polyfillPluralRules, preloadTranslations } from "./src/utils/intl";
import metrics from "./src/utils/metrics";
import { initializeAmplitude } from "./src/utils/amplitude";

// Services
import launchDarkly from "./src/services/launchDarkly";

// Store
import { getStore } from "./src/store/createStore";
import { updateRoute } from "./src/store/route/actions";
import InitializeStore from "./src/components/global/InitializeStore";
import InitializeLocale from "./src/components/global/InitializeLocale";

// Animations
import { TweenMax } from "gsap";
import ScrollMagic from "scrollmagic";
import ScrollMagicGsapPlugin from "ScrollMagicGsapPlugin";

import { defineCustomElements } from "@ritual/essentials-for-react";

import GlobalStyle from "./src/styles/global";

export const wrapRootElement = ({ element }) => {
  defineCustomElements();

  Sentry.init({
    dsn: process.env.GATSBY_SENTRY_DSN,
    environment: process.env.GATSBY_ACTIVE_ENV || "development",
    release: process.env.GATSBY_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/api\.ritual\.com\/api/,
          /^https:\/\/api\.seg\.ritual\.com\/.*/,
          /^https:\/\/(?:.+\.)?ritual\.engineering\/api/,
        ],
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.launchDarklyIntegration(),
    ],
    // Tracing
    tracesSampleRate:
      parseFloat(process.env.GATSBY_SENTRY_TRACE_SAMPLE_RATE) || 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/api\.ritual\.com\/api/,
      /^https:\/\/(?:.+\.)?ritual\.engineering\/api/,
    ],
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // result in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });

  // Instantiating store in `wrapRootElement` handler ensures:
  //  - there is fresh store for each SSR page
  //  - it will be called only once in browser, when React mounts
  const store = getStore();

  return (
    <Sentry.ErrorBoundary>
      <GlobalStyle />
      <InitializeStore store={store}>
        <Provider store={store}>{element}</Provider>
      </InitializeStore>
    </Sentry.ErrorBoundary>
  );
};

export const onClientEntry = async () => {
  // Initializes ScrollMagic with the GSAP animations plugin.
  ScrollMagicGsapPlugin(ScrollMagic, TweenMax);

  await Promise.all([
    require(`intersection-observer`),
    // Optionally load Intl.PluralRules if not supported natively
    polyfillPluralRules(),
    // Delay rendering until translation data has been grabbed. This ensures
    // there is no flash of missing content when rehydrating from the server.
    preloadTranslations(),

    // Delay rendering until Launch Darkly has been initialized. This ensures
    // that the initial content is rendered with correct feature values instead
    // of default values, which prevents flickering.

    launchDarkly.initialize(),
  ]);

  // Initialize Amplitude (non-blocking, consent-gated)
  // This runs after the page has loaded to avoid blocking initial render
  initializeAmplitude().catch((error) => {
    // Error is already captured in initializeAmplitude, just prevent unhandled rejection
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("[Amplitude] Initialization error:", error);
    }
  });
};

export const onInitialClientRender = () => {
  const userInfo = analyticsService.getStoredUserInfo();

  Sentry.setUser({
    id: userInfo.id || null,
    email: userInfo.traits.email || null,
  });

  // @axe-core/react implementation
  if (
    process.env.NODE_ENV === "development" &&
    process.env.ENABLE_AXE_CORE === "true"
  ) {
    import("@axe-core/react").then((axe) => {
      axe.default(React, ReactDOM, 1000);
    });
  }
};

export const wrapPageElement = ({ element, props }) => {
  const { locale, activeLocales } = props.pageContext;
  return (
    <InitializeLocale locale={locale} activeLocales={activeLocales}>
      {element}
    </InitializeLocale>
  );
};

export const onPreRouteUpdate = ({ location, prevLocation }) => {
  // Reset the page label on transitions.
  currentPageData.label = null;
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  const pageLabel = currentPageData.label;

  getStore().dispatch(updateRoute({ location, prevLocation }));

  if (!window.analytics || typeof window.analytics.page !== "function") {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("Unable to locate analytics.js");
    }
    return;
  }

  metrics.page(pageLabel);
};
